/**
 * Компонент выпадающего меню контактов
 */

import { useEffect, useRef } from 'react';
import { Phone, MessageCircle, Send, Mail } from 'lucide-react';
import styles from './ContactsDropdown.module.css';
import { useConfigStore } from '@/store/configStore';

/**
 * Пропсы компонента ContactsDropdown
 */
interface ContactsDropdownProps {
  /** Колбек при закрытии */
  onClose: () => void;
}

/**
 * Выпадающее меню с контактами
 * 
 * @description Показывает быстрые действия: телефон, WhatsApp, Telegram, Email, сайт
 */
export function ContactsDropdown({ onClose }: ContactsDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Получаем контакты из store (настраиваемые)
  const { contacts } = useConfigStore();

  // Закрытие при клике вне dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Небольшая задержка чтобы не закрылось сразу при открытии
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Закрытие при Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div ref={dropdownRef} className={styles.dropdown} role="menu">
      {/* Header */}
      <div className={styles.dropdownHeader}>Контакты</div>

      {/* Телефон */}
      {contacts.phone && (
        <a
          href={contacts.phoneLink || `tel:${contacts.phone}`}
          className={styles.contactItem}
          role="menuitem"
          onClick={onClose}
        >
          <span className={styles.contactIcon}>
            <Phone size={16} strokeWidth={2} />
          </span>
          <span className={styles.contactText}>{contacts.phone}</span>
        </a>
      )}

      {/* WhatsApp */}
      {contacts.whatsapp && (
        <a
          href={contacts.whatsapp}
          className={styles.contactItem}
          target="_blank"
          rel="noopener noreferrer"
          role="menuitem"
          onClick={onClose}
        >
          <span className={styles.contactIcon}>
            <MessageCircle size={16} strokeWidth={2} />
          </span>
          <span className={styles.contactText}>WhatsApp</span>
        </a>
      )}

      {/* Telegram */}
      {contacts.telegram && (
        <a
          href={contacts.telegram}
          className={styles.contactItem}
          target="_blank"
          rel="noopener noreferrer"
          role="menuitem"
          onClick={onClose}
        >
          <span className={styles.contactIcon}>
            <Send size={16} strokeWidth={2} />
          </span>
          <span className={styles.contactText}>Telegram</span>
        </a>
      )}

      {/* Email */}
      {contacts.email && (
        <a
          href={contacts.emailLink || `mailto:${contacts.email}`}
          className={styles.contactItem}
          role="menuitem"
          onClick={onClose}
        >
          <span className={styles.contactIcon}>
            <Mail size={16} strokeWidth={2} />
          </span>
          <span className={styles.contactText}>{contacts.email}</span>
        </a>
      )}
    </div>
  );
}

